import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  ChevronLeft, 
  ChevronRight,
  Play,
  Pause,
  RotateCcw,
  Award,
  Target,
  BookOpen
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Progress } from '../../ui/progress';
import { Badge } from '../../ui/badge';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { Textarea } from '../../ui/textarea';
import { useToast } from "../../../hooks/useToast";
import { useTenantContext } from "../../../context/TenantContext";
import QuizFeedbackSystem from './QuizFeedbackSystem';
import { 
  Quiz, 
  QuizQuestion, 
  QuizAnswer, 
  QuizAttempt, 
  StudentAnswer,
  getQuizByLessonId,
  startQuizAttempt,
  submitStudentAnswer,
  completeQuizAttempt,
  formatQuizTime,
  randomizeQuizQuestions,
  randomizeAnswers
} from '../../../services/elearningQuizService';

interface QuizPlayerProps {
  lessonId: string;
  studentId: string;
  enrollmentId: string;
  onQuizComplete: (score: number, passed: boolean) => void;
  onClose: () => void;
}

interface QuizState {
  quiz: Quiz | null;
  currentQuestionIndex: number;
  answers: Record<string, any>;
  timeRemaining: number;
  isSubmitting: boolean;
  showResults: boolean;
  attempt: QuizAttempt | null;
  immediateResults: Record<string, {
    isCorrect: boolean;
    pointsEarned: number;
    feedback: string;
  }>;
}

const QuizPlayer: React.FC<QuizPlayerProps> = ({
  lessonId,
  studentId,
  enrollmentId,
  onQuizComplete,
  onClose
}) => {
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [state, setState] = useState<QuizState>({
    quiz: null,
    currentQuestionIndex: 0,
    answers: {},
    timeRemaining: 0,
    isSubmitting: false,
    showResults: false,
    attempt: null,
    immediateResults: {} // Pour stocker les résultats immédiats par question
  });

  const [loading, setLoading] = useState(true);
  const [timerActive, setTimerActive] = useState(false);
  const [showCorrections, setShowCorrections] = useState(false);
  
  // Fonction pour évaluer les conditions des questions
  const evaluateCondition = (answer: any, operator: string, value: any): boolean => {
    switch (operator) {
      case 'equals':
        return answer === value;
      case 'not_equals':
        return answer !== value;
      case 'contains':
        return Array.isArray(answer) ? answer.includes(value) : false;
      case 'not_contains':
        return Array.isArray(answer) ? !answer.includes(value) : true;
      case 'greater_than':
        return Number(answer) > Number(value);
      case 'less_than':
        return Number(answer) < Number(value);
      default:
        return true;
    }
  };
  
  // Fonction pour trouver la prochaine question valide
  const findNextValidQuestion = (startIndex: number): number => {
    if (!state.quiz?.questions) return startIndex;
    
    for (let i = startIndex; i < state.quiz.questions.length; i++) {
      const question = state.quiz.questions[i];
      
      // Si la question n'est pas conditionnelle, elle est valide
      if (!question.conditional_logic?.enabled) {
        return i;
      }
      
      // Vérifier si la condition est remplie
      const condition = question.conditional_logic;
      const dependentAnswer = state.answers[condition.dependent_question_id];
      
      if (evaluateCondition(dependentAnswer, condition.operator, condition.value)) {
        return i;
      }
    }
    
    return startIndex; // Retourner l'index de départ si aucune question valide n'est trouvée
  };
  


  // Charger le quiz
  useEffect(() => {
    // Utiliser une variable pour suivre si le composant est monté
    let isMounted = true;
    
    const loadQuiz = async () => {
      try {
        setLoading(true);
        const quiz = await getQuizByLessonId(lessonId, tenant!.id);
        
        // Vérifier si le composant est toujours monté avant de mettre à jour l'état
        if (!isMounted) return;
        
        if (!quiz) {
          toast({
            title: "Erreur",
            description: "Aucun quiz trouvé pour cette leçon.",
            variant: "destructive"
          });
          onClose();
          return;
        }

        // Mélanger les questions si nécessaire
        const questions = quiz.is_randomized && quiz.questions 
          ? randomizeQuizQuestions(quiz.questions)
          : quiz.questions || [];

        // Mélanger les réponses pour chaque question
        const processedQuestions = questions.map(question => ({
          ...question,
          answers: question.answers ? randomizeAnswers(question.answers) : []
        }));

        const processedQuiz = {
          ...quiz,
          questions: processedQuestions
        };

        // Vérifier les paramètres du quiz dans la console
        console.log('Quiz chargé avec les paramètres:', {
          show_correct_answers: processedQuiz.show_correct_answers,
          show_results_immediately: processedQuiz.show_results_immediately
        });
        
        setState(prev => ({
          ...prev,
          quiz: processedQuiz,
          timeRemaining: quiz.time_limit ? quiz.time_limit * 60 : 0
        }));

        // Démarrer une tentative
        const attempt = await startQuizAttempt(quiz.id, studentId, enrollmentId, tenant!.id);
        
        // Vérifier à nouveau si le composant est monté
        if (!isMounted) return;
        
        setState(prev => ({
          ...prev,
          attempt
        }));

        if (quiz.time_limit) {
          setTimerActive(true);
        }

      } catch (error) {
        console.error('Erreur lors du chargement du quiz:', error);
        if (isMounted) {
          toast({
            title: "Erreur",
            description: "Impossible de charger le quiz.",
            variant: "destructive"
          });
          onClose();
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (tenant) {
      loadQuiz();
    }
    
    // Fonction de nettoyage pour éviter les mises à jour d'état sur un composant démonté
    return () => {
      isMounted = false;
    };
  }, [lessonId, studentId, enrollmentId, tenant, onClose]);

  // Soumettre le quiz
  const handleSubmitQuiz = useCallback(async () => {
    console.log('handleSubmitQuiz - state.attempt:', state.attempt);
    if (!state.attempt || state.isSubmitting) return;
    
    // Vérification supplémentaire pour l'ID de tentative
    if (!state.attempt.id) {
      console.error('ID de tentative non défini');
      toast({
        title: "Erreur",
        description: "Impossible de soumettre le quiz: ID de tentative manquant.",
        variant: "destructive"
      });
      return;
    }

    setState(prev => ({ ...prev, isSubmitting: true }));
    setTimerActive(false);

    try {
      console.log('Tentative de soumission du quiz avec ID:', state.attempt.id);
      const completedAttempt = await completeQuizAttempt(state.attempt.id, tenant!.id);
      
      setState(prev => ({
        ...prev,
        attempt: completedAttempt,
        showResults: true
      }));

      onQuizComplete(completedAttempt.score, completedAttempt.passed);

      toast({
        title: completedAttempt.passed ? "Félicitations !" : "Quiz terminé",
        description: completedAttempt.passed 
          ? `Vous avez réussi avec ${completedAttempt.percentage}% !`
          : `Vous avez obtenu ${completedAttempt.percentage}%. Score requis : ${state.quiz?.passing_score}%`,
        variant: completedAttempt.passed ? "default" : "destructive"
      });

    } catch (error) {
      console.error('Erreur lors de la soumission du quiz:', error);
      toast({
        title: "Erreur",
        description: "Impossible de soumettre le quiz.",
        variant: "destructive"
      });
    } finally {
      setState(prev => ({ ...prev, isSubmitting: false }));
    }
  }, [state.attempt, state.quiz?.passing_score, state.isSubmitting, tenant, onQuizComplete, toast]);

  // Timer
  useEffect(() => {
    if (!timerActive || state.timeRemaining <= 0) return;

    const timer = setInterval(() => {
      setState(prev => {
        const newTimeRemaining = prev.timeRemaining - 1;
        if (newTimeRemaining <= 0) {
          setTimerActive(false);
          handleSubmitQuiz();
          return { ...prev, timeRemaining: 0 };
        }
        return { ...prev, timeRemaining: newTimeRemaining };
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timerActive, state.timeRemaining, handleSubmitQuiz]);

  // Gérer la réponse à une question
  const handleAnswerChange = useCallback(async (questionId: string, value: any) => {
    // Mettre à jour l'état local
    setState(prev => ({
      ...prev,
      answers: {
        ...prev.answers,
        [questionId]: value
      }
    }));
    
    // Sauvegarder la réponse dans la base de données
    if (state.attempt) {
      console.log('Sauvegarde de la réponse:', { questionId, value });
      console.log('Paramètres du quiz:', {
        show_correct_answers: state.quiz?.show_correct_answers,
        show_results_immediately: state.quiz?.show_results_immediately
      });
      if (!state.attempt.id) {
        console.error('handleAnswerChange: ID de tentative non défini');
        toast({
          title: "Attention",
          description: "Impossible de sauvegarder la réponse: ID de tentative manquant.",
          variant: "destructive"
        });
        return;
      }
      
      try {
        console.log('Sauvegarde de la réponse pour la tentative:', state.attempt.id);
        const studentAnswer = await submitStudentAnswer(
          state.attempt.id,
          questionId,
          {
            answer_id: typeof value === 'string' ? value : undefined,
            text_answer: typeof value === 'string' && !value.startsWith('answer_') ? value : undefined,
            selected_answers: Array.isArray(value) ? value : undefined,
            tenant_id: tenant!.id
          },
          tenant!.id
        );
        
        // Afficher les résultats immédiatement si l'option est activée
        if (state.quiz?.show_results_immediately === true && studentAnswer) {
          // Mettre à jour l'état pour refléter le résultat immédiat
          setState(prev => ({
            ...prev,
            immediateResults: {
              ...prev.immediateResults,
              [questionId]: {
                isCorrect: studentAnswer.is_correct,
                pointsEarned: studentAnswer.points_earned,
                feedback: studentAnswer.is_correct 
                  ? "Bonne réponse !" 
                  : "Ce n'est pas la bonne réponse."
              }
            }
          }));
          
          // Afficher un toast avec le résultat
          toast({
            title: studentAnswer.is_correct ? "Bonne réponse !" : "Réponse incorrecte",
            description: studentAnswer.is_correct 
              ? "Vous avez choisi la bonne réponse." 
              : "Ce n'est pas la bonne réponse.",
            variant: studentAnswer.is_correct ? "default" : "destructive"
          });
        }
      } catch (error) {
        console.error('Erreur lors de la sauvegarde de la réponse:', error);
        toast({
          title: "Erreur",
          description: "Impossible de sauvegarder la réponse.",
          variant: "destructive"
        });
      }
    }
  }, [state.attempt, state.quiz?.show_results_immediately, tenant, toast]);

  // Navigation entre questions avec support des questions conditionnelles
  const goToQuestion = useCallback((index: number) => {
    if (!state.quiz?.questions || index < 0 || index >= state.quiz.questions.length) {
      return;
    }
    
    const targetQuestion = state.quiz.questions[index];
    
    // Vérifier si la question est conditionnelle
    if (targetQuestion.conditional_logic?.enabled) {
      const condition = targetQuestion.conditional_logic;
      const dependentAnswer = state.answers[condition.dependent_question_id];
      
      // Si la condition n'est pas remplie, trouver la prochaine question valide
      if (!evaluateCondition(dependentAnswer, condition.operator, condition.value)) {
        const nextValidIndex = findNextValidQuestion(index + 1);
        if (nextValidIndex !== index && nextValidIndex < state.quiz.questions.length) {
          setState(prev => ({ ...prev, currentQuestionIndex: nextValidIndex }));
          return;
        }
      }
    }
    
    setState(prev => ({ ...prev, currentQuestionIndex: index }));
  }, [state.quiz?.questions, state.answers]);

  // Rendu des réponses selon le type de question
  const renderAnswerInput = (question: QuizQuestion) => {
    const currentAnswer = state.answers[question.id];

    switch (question.question_type) {
      case 'single_choice':
        return (
          <RadioGroup
            value={currentAnswer || ''}
            onValueChange={(value) => handleAnswerChange(question.id, value)}
            className="space-y-3"
          >
            {question.answers?.map((answer) => (
              <div key={answer.id} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 transition-colors">
                <RadioGroupItem value={answer.id} id={answer.id} className="border-white/30" />
                <Label htmlFor={answer.id} className="flex-1 cursor-pointer text-white">
                  {answer.answer_text}
                </Label>
                {/* Afficher un indicateur si les résultats immédiats sont activés et que l'utilisateur a répondu */}
                {state.quiz?.show_results_immediately === true && 
                 state.immediateResults[question.id] && 
                 currentAnswer === answer.id && (
                  <div className={`ml-2 ${state.immediateResults[question.id].isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                    {state.immediateResults[question.id].isCorrect ? 
                      <CheckCircle className="w-5 h-5" /> : 
                      <X className="w-5 h-5" />
                    }
                  </div>
                )}
              </div>
            ))}
          </RadioGroup>
        );

      case 'multiple_choice':
        return (
          <div className="space-y-3">
            {question.answers?.map((answer) => (
              <div key={answer.id} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 transition-colors">
                <Checkbox
                  id={answer.id}
                  checked={(currentAnswer || []).includes(answer.id)}
                  onCheckedChange={(checked) => {
                    const current = currentAnswer || [];
                    const updated = checked
                      ? [...current, answer.id]
                      : current.filter((id: string) => id !== answer.id);
                    handleAnswerChange(question.id, updated);
                  }}
                  className="border-white/30"
                />
                <Label htmlFor={answer.id} className="flex-1 cursor-pointer text-white">
                  {answer.answer_text}
                </Label>
                {/* Afficher un indicateur si les résultats immédiats sont activés et que l'utilisateur a répondu */}
                {state.quiz?.show_results_immediately === true && 
                 state.immediateResults[question.id] && 
                 (currentAnswer || []).includes(answer.id) && (
                  <div className={`ml-2 ${answer.is_correct ? 'text-green-400' : 'text-red-400'}`}>
                    {answer.is_correct ? 
                      <CheckCircle className="w-5 h-5" /> : 
                      <X className="w-5 h-5" />
                    }
                  </div>
                )}
              </div>
            ))}
          </div>
        );

      case 'true_false':
        return (
          <RadioGroup
            value={currentAnswer || ''}
            onValueChange={(value) => handleAnswerChange(question.id, value)}
            className="space-y-3"
          >
            <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 transition-colors">
              <RadioGroupItem value="true" id="true" className="border-white/30" />
              <Label htmlFor="true" className="flex-1 cursor-pointer text-white">Vrai</Label>
            </div>
            <div className="flex items-center space-x-3 p-3 rounded-lg hover:bg-white/5 transition-colors">
              <RadioGroupItem value="false" id="false" className="border-white/30" />
              <Label htmlFor="false" className="flex-1 cursor-pointer text-white">Faux</Label>
            </div>
          </RadioGroup>
        );

      case 'text_input':
        return (
          <Textarea
            value={currentAnswer || ''}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder="Saisissez votre réponse..."
            className="bg-white/5 border-white/20 text-white placeholder:text-white/50 min-h-[100px] resize-none"
          />
        );

      default:
        return <div className="text-white/50">Type de question non supporté</div>;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center">
        <div className="bg-gradient-to-br from-gray-900 to-black p-8 rounded-2xl border border-white/10">
          <div className="flex items-center space-x-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500"></div>
            <span className="text-white font-medium">Chargement du quiz...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!state.quiz) {
    return null;
  }

  const currentQuestion = state.quiz.questions?.[state.currentQuestionIndex];
  const totalQuestions = state.quiz.questions?.length || 0;
  const progress = totalQuestions > 0 ? ((state.currentQuestionIndex + 1) / totalQuestions) * 100 : 0;

  return (
    <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="w-full max-w-4xl max-h-[90vh] overflow-hidden"
      >
        <Card className="bg-gradient-to-br from-gray-900 to-black border-white/10 text-white">
          <CardHeader className="border-b border-white/10 bg-gradient-to-r from-red-600/10 to-red-500/5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="bg-red-600/20 p-2 rounded-lg">
                  <BookOpen size={24} className="text-red-400" />
                </div>
                <div>
                  <CardTitle className="text-xl font-bold">{state.quiz.title}</CardTitle>
                  {state.quiz.description && (
                    <p className="text-white/70 text-sm mt-1">{state.quiz.description}</p>
                  )}
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                {state.quiz.time_limit && (
                  <div className="flex items-center space-x-2 bg-black/30 px-3 py-2 rounded-lg">
                    <Clock size={16} className={state.timeRemaining < 300 ? "text-red-400" : "text-white/70"} />
                    <span className={`font-mono ${state.timeRemaining < 300 ? "text-red-400" : "text-white"}`}>
                      {formatQuizTime(state.timeRemaining)}
                    </span>
                  </div>
                )}
                
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="text-white hover:bg-white/10 rounded-full h-8 w-8 p-0"
                >
                  <X size={16} />
                </Button>
              </div>
            </div>
            
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-white/70">Question {state.currentQuestionIndex + 1} sur {totalQuestions}</span>
                <span className="text-white/70">{Math.round(progress)}% complété</span>
              </div>
              <Progress value={progress} className="h-2 bg-white/10" />
            </div>
          </CardHeader>

          <CardContent className="p-6">
            {state.showResults ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center space-y-6"
              >
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-8 rounded-2xl border border-white/10">
                  <div className="flex justify-center mb-4">
                    {state.attempt?.passed ? (
                      <div className="bg-green-500/20 p-4 rounded-full">
                        <Award size={48} className="text-green-400" />
                      </div>
                    ) : (
                      <div className="bg-red-500/20 p-4 rounded-full">
                        <Target size={48} className="text-red-400" />
                      </div>
                    )}
                  </div>
                  
                  <h3 className="text-2xl font-bold mb-2">
                    {state.attempt?.passed ? "Félicitations !" : "Quiz terminé"}
                  </h3>
                  
                  <p className="text-white/70 mb-6">
                    {state.attempt?.passed 
                      ? "Vous avez réussi ce quiz avec succès !"
                      : "Vous pouvez réessayer pour améliorer votre score."}
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-black/30 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-blue-400">{state.attempt?.score}</div>
                      <div className="text-sm text-white/70">Score obtenu</div>
                    </div>
                    <div className="bg-black/30 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-green-400">{state.attempt?.percentage}%</div>
                      <div className="text-sm text-white/70">Pourcentage</div>
                    </div>
                    <div className="bg-black/30 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-yellow-400">{state.quiz.passing_score}%</div>
                      <div className="text-sm text-white/70">Score requis</div>
                    </div>
                    <div className="bg-black/30 p-4 rounded-lg">
                      <div className="text-2xl font-bold text-purple-400">
                        {state.attempt?.completed_at && state.attempt?.started_at 
                          ? Math.round((new Date(state.attempt.completed_at).getTime() - new Date(state.attempt.started_at).getTime()) / 60000)
                          : 'N/A'}
                      </div>
                      <div className="text-sm text-white/70">Minutes</div>
                    </div>
                  </div>
                  
                  {/* Statistiques détaillées par type de question */}
                  {state.quiz?.questions && state.quiz.questions.length > 0 && (
                    <div className="bg-black/20 p-4 rounded-lg mb-6">
                      <h4 className="text-lg font-semibold text-white mb-4">Analyse détaillée</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-white/70">Questions répondues :</span>
                            <span className="text-white font-medium">
                              {Object.keys(state.answers).length} / {state.quiz.questions.length}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/70">Taux de réussite :</span>
                            <span className={`font-medium ${
                              state.attempt?.passed ? 'text-green-400' : 'text-red-400'
                            }`}>
                              {state.attempt?.passed ? 'Réussi' : 'Échoué'}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-white/70">Points maximum :</span>
                            <span className="text-white font-medium">
                              {state.quiz.questions.reduce((total, q) => total + (q.points || 1), 0)}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-white/70">Difficulté moyenne :</span>
                            <span className="text-orange-400 font-medium">
                              {state.quiz.questions.length > 0 ? 'Modérée' : 'N/A'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Bouton pour afficher/masquer les corrections - seulement si l'option est activée */}
                  {state.quiz?.show_correct_answers === true && (
                    <div className="mb-4">
                      <Button
                        onClick={() => setShowCorrections(!showCorrections)}
                        variant="outline"
                        className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10 w-full"
                      >
                        {showCorrections ? 'Masquer les corrections' : 'Voir les corrections détaillées'}
                        <ChevronRight className={`w-4 h-4 ml-2 transition-transform ${
                          showCorrections ? 'rotate-90' : ''
                        }`} />
                      </Button>
                    </div>
                  )}
                  
                  {/* Affichage des corrections et bonnes réponses - seulement si l'option est activée */}
                  {state.quiz?.show_correct_answers === true && showCorrections && state.quiz.questions && (
                    <div className="bg-black/20 p-6 rounded-lg mb-6">
                      <h4 className="text-xl font-semibold text-white mb-6 flex items-center">
                        <CheckCircle className="w-5 h-5 mr-2 text-green-400" />
                        Corrections détaillées
                      </h4>
                      <div className="space-y-6">
                        {state.quiz.questions.map((question, index) => {
                          const userAnswer = state.answers[question.id];
                          const correctAnswers = question.answers?.filter(a => a.is_correct) || [];
                          
                          return (
                            <motion.div
                              key={question.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: index * 0.1 }}
                              className="bg-gray-800/50 p-4 rounded-lg border border-white/10"
                            >
                              <div className="flex items-start justify-between mb-3">
                                <h5 className="font-medium text-white text-sm">
                                  Question {index + 1}: {question.question_text}
                                </h5>
                                <Badge 
                                  variant="outline" 
                                  className={`${
                                    // Vérifier si la réponse est correcte
                                    question.question_type === 'single_choice' || question.question_type === 'true_false'
                                      ? correctAnswers.some(ca => ca.id === userAnswer)
                                        ? 'border-green-500/30 text-green-400 bg-green-500/10'
                                        : 'border-red-500/30 text-red-400 bg-red-500/10'
                                      : question.question_type === 'multiple_choice'
                                      ? Array.isArray(userAnswer) && correctAnswers.length > 0 &&
                                        correctAnswers.every(ca => userAnswer.includes(ca.id)) &&
                                        userAnswer.every(ua => correctAnswers.some(ca => ca.id === ua))
                                        ? 'border-green-500/30 text-green-400 bg-green-500/10'
                                        : 'border-red-500/30 text-red-400 bg-red-500/10'
                                      : 'border-gray-500/30 text-gray-400 bg-gray-500/10'
                                  }`}
                                >
                                  {question.question_type === 'text_input' ? 'Réponse libre' :
                                   (question.question_type === 'single_choice' || question.question_type === 'true_false'
                                     ? correctAnswers.some(ca => ca.id === userAnswer) ? 'Correct' : 'Incorrect'
                                     : question.question_type === 'multiple_choice'
                                     ? Array.isArray(userAnswer) && correctAnswers.length > 0 &&
                                       correctAnswers.every(ca => userAnswer.includes(ca.id)) &&
                                       userAnswer.every(ua => correctAnswers.some(ca => ca.id === ua))
                                       ? 'Correct' : 'Incorrect'
                                     : 'N/A')}
                                </Badge>
                              </div>
                              
                              {/* Affichage des réponses */}
                              <div className="space-y-2 text-sm">
                                {question.answers?.map(answer => {
                                  const isUserAnswer = 
                                    question.question_type === 'single_choice' || question.question_type === 'true_false'
                                      ? userAnswer === answer.id
                                      : question.question_type === 'multiple_choice'
                                      ? Array.isArray(userAnswer) && userAnswer.includes(answer.id)
                                      : false;
                                  
                                  return (
                                    <div 
                                      key={answer.id}
                                      className={`p-2 rounded border ${
                                        answer.is_correct
                                          ? 'border-green-500/30 bg-green-500/10 text-green-300'
                                          : isUserAnswer
                                          ? 'border-red-500/30 bg-red-500/10 text-red-300'
                                          : 'border-gray-600/30 bg-gray-600/10 text-gray-400'
                                      }`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <span>{answer.answer_text}</span>
                                        <div className="flex items-center space-x-2">
                                          {answer.is_correct && (
                                            <CheckCircle className="w-4 h-4 text-green-400" />
                                          )}
                                          {isUserAnswer && !answer.is_correct && (
                                            <X className="w-4 h-4 text-red-400" />
                                          )}
                                          {isUserAnswer && (
                                            <span className="text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded">
                                              Votre réponse
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      {answer.explanation && (
                                        <div className="mt-2 text-xs text-white/70 italic">
                                          Explication : {answer.explanation}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                
                                {/* Pour les questions de type texte */}
                                {question.question_type === 'text_input' && (
                                  <div className="space-y-2">
                                    <div className="p-2 rounded border border-blue-500/30 bg-blue-500/10">
                                      <span className="text-xs text-blue-300">Votre réponse :</span>
                                      <div className="text-white mt-1">{userAnswer || 'Aucune réponse'}</div>
                                    </div>
                                    {question.correct_answer && (
                                      <div className="p-2 rounded border border-green-500/30 bg-green-500/10">
                                        <span className="text-xs text-green-300">Réponse suggérée :</span>
                                        <div className="text-green-200 mt-1">{question.correct_answer}</div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <Button
                    onClick={onClose}
                    className="bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white px-8 py-3 rounded-lg font-medium"
                  >
                    Continuer le cours
                  </Button>
                </div>
              </motion.div>
            ) : currentQuestion ? (
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-gray-800 to-gray-900 p-6 rounded-xl border border-white/10">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex gap-2 flex-wrap">
                      <Badge variant="outline" className="border-red-500/30 text-red-400 bg-red-500/10">
                        {currentQuestion.points} point{currentQuestion.points > 1 ? 's' : ''}
                      </Badge>
                      {currentQuestion.is_required && (
                        <Badge variant="outline" className="border-yellow-500/30 text-yellow-400 bg-yellow-500/10">
                          Obligatoire
                        </Badge>
                      )}
                      {/* Indicateur de difficulté basé sur les points */}
                      {currentQuestion.points && (
                        <Badge 
                          variant="outline" 
                          className={`${
                            currentQuestion.points <= 1 
                              ? 'border-green-500/30 text-green-400 bg-green-500/10'
                              : currentQuestion.points <= 3
                              ? 'border-orange-500/30 text-orange-400 bg-orange-500/10'
                              : 'border-red-500/30 text-red-400 bg-red-500/10'
                          }`}
                        >
                          {currentQuestion.points <= 1 ? 'Facile' : currentQuestion.points <= 3 ? 'Moyen' : 'Difficile'}
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <h3 className="text-lg font-semibold mb-4 leading-relaxed">
                    {currentQuestion.question_text}
                  </h3>
                  
                  {/* Affichage des tags et catégories */}
                  {(currentQuestion.tags && currentQuestion.tags.length > 0) && (
                    <div className="flex flex-wrap gap-2 mb-4">
                      {currentQuestion.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary" className="bg-blue-500/20 text-blue-300 border-blue-500/30">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                  
                  {currentQuestion.media_url && (
                    <div className="mb-4">
                      {currentQuestion.media_type === 'image' && (
                        <img 
                          src={currentQuestion.media_url} 
                          alt="Question media"
                          className="max-w-full h-auto rounded-lg border border-white/10"
                        />
                      )}
                      {currentQuestion.media_type === 'video' && (
                        <video 
                          src={currentQuestion.media_url} 
                          controls
                          className="max-w-full h-auto rounded-lg border border-white/10"
                        />
                      )}
                      {currentQuestion.media_type === 'audio' && (
                        <audio 
                          src={currentQuestion.media_url} 
                          controls
                          className="w-full rounded-lg border border-white/10 bg-white/5"
                        />
                      )}
                    </div>
                  )}
                  
                  <div className="mt-6">
                    {renderAnswerInput(currentQuestion)}
                  </div>
                  
                  {/* Affichage des feedbacks personnalisés */}
                  {state.answers[currentQuestion.id] && currentQuestion.answers && (
                    <div className="mt-4 space-y-3">
                      <h4 className="font-medium text-white/90 text-sm">Feedback :</h4>
                      {currentQuestion.answers.map(answer => {
                        const isSelected = 
                          (currentQuestion.question_type === 'single_choice' || currentQuestion.question_type === 'true_false') 
                            ? state.answers[currentQuestion.id] === answer.id
                            : currentQuestion.question_type === 'multiple_choice'
                            ? Array.isArray(state.answers[currentQuestion.id]) && state.answers[currentQuestion.id].includes(answer.id)
                            : false;
                        
                        if (isSelected && answer.feedback && answer.feedback.trim()) {
                          return (
                            <motion.div
                              key={answer.id}
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="bg-blue-500/10 border border-blue-500/20 p-3 rounded-lg"
                            >
                              <p className="text-sm text-blue-200">{answer.feedback}</p>
                            </motion.div>
                          );
                        }
                        return null;
                      })}
                    </div>
                  )}
                  
                  {/* Affichage des corrections immédiates si activé */}
                  {state.quiz?.show_immediate_results && state.answers[currentQuestion.id] && currentQuestion.answers && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mt-4 bg-gray-800/50 p-4 rounded-lg border border-white/10"
                    >
                      <h4 className="font-medium text-white/90 text-sm mb-3 flex items-center">
                        <Target className="w-4 h-4 mr-2" />
                        Correction immédiate
                      </h4>
                      <div className="space-y-2 text-sm">
                        {currentQuestion.answers.map(answer => {
                          const isSelected = 
                            (currentQuestion.question_type === 'single_choice' || currentQuestion.question_type === 'true_false') 
                              ? state.answers[currentQuestion.id] === answer.id
                              : currentQuestion.question_type === 'multiple_choice'
                              ? Array.isArray(state.answers[currentQuestion.id]) && state.answers[currentQuestion.id].includes(answer.id)
                              : false;
                          
                          return (
                            <div 
                              key={answer.id}
                              className={`p-2 rounded border text-xs ${
                                answer.is_correct
                                  ? 'border-green-500/30 bg-green-500/10 text-green-300'
                                  : isSelected
                                  ? 'border-red-500/30 bg-red-500/10 text-red-300'
                                  : 'border-gray-600/30 bg-gray-600/10 text-gray-400'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <span>{answer.answer_text}</span>
                                <div className="flex items-center space-x-1">
                                  {answer.is_correct && (
                                    <CheckCircle className="w-3 h-3 text-green-400" />
                                  )}
                                  {isSelected && !answer.is_correct && (
                                    <X className="w-3 h-3 text-red-400" />
                                  )}
                                  {isSelected && (
                                    <span className="text-xs bg-blue-500/20 text-blue-300 px-1 py-0.5 rounded">
                                      Vous
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Indicateur de réussite */}
                      <div className="mt-3 flex items-center justify-center">
                        {(() => {
                          const correctAnswers = currentQuestion.answers?.filter(a => a.is_correct) || [];
                          const userAnswer = state.answers[currentQuestion.id];
                          const isCorrect = 
                            currentQuestion.question_type === 'single_choice' || currentQuestion.question_type === 'true_false'
                              ? correctAnswers.some(ca => ca.id === userAnswer)
                              : currentQuestion.question_type === 'multiple_choice'
                              ? Array.isArray(userAnswer) && correctAnswers.length > 0 &&
                                correctAnswers.every(ca => userAnswer.includes(ca.id)) &&
                                userAnswer.every(ua => correctAnswers.some(ca => ca.id === ua))
                              : false;
                          
                          return (
                            <Badge 
                              variant="outline" 
                              className={`${
                                isCorrect
                                  ? 'border-green-500/30 text-green-400 bg-green-500/10'
                                  : 'border-red-500/30 text-red-400 bg-red-500/10'
                              }`}
                            >
                              {isCorrect ? '✓ Correct' : '✗ Incorrect'}
                            </Badge>
                          );
                        })()}
                      </div>
                    </motion.div>
                  )}
                </div>
                
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    onClick={() => goToQuestion(state.currentQuestionIndex - 1)}
                    disabled={state.currentQuestionIndex === 0}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    <ChevronLeft size={16} className="mr-2" />
                    Précédent
                  </Button>
                  
                  <div className="flex space-x-2">
                    {Array.from({ length: totalQuestions }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => goToQuestion(i)}
                        className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                          i === state.currentQuestionIndex
                            ? 'bg-red-600 text-white'
                            : state.answers[state.quiz!.questions![i].id]
                            ? 'bg-green-600/30 text-green-400 border border-green-500/30'
                            : 'bg-white/10 text-white/70 hover:bg-white/20'
                        }`}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                  
                  {state.currentQuestionIndex === totalQuestions - 1 ? (
                    <Button
                      onClick={handleSubmitQuiz}
                      disabled={state.isSubmitting}
                      className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white"
                    >
                      {state.isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Soumission...
                        </>
                      ) : (
                        <>
                          Terminer le quiz
                          <CheckCircle size={16} className="ml-2" />
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => goToQuestion(state.currentQuestionIndex + 1)}
                      className="border-white/20 text-white hover:bg-white/10"
                    >
                      Suivant
                      <ChevronRight size={16} className="ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertCircle size={48} className="text-yellow-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Aucune question trouvée</h3>
                <p className="text-white/70">Ce quiz ne contient aucune question.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default QuizPlayer;
